import React, { forwardRef, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useDropdownFilter } from "../Custom_Hooks/useDropdownFilter";
import { useDropdownKeyboardNavigation } from "../Custom_Hooks/useDropdownKeyboardNavigation";
import { useDropdownScroll } from "../Custom_Hooks/useDropdownScroll";
import { useDispatch, useSelector } from "react-redux";
import {
  closeModal,
  openModal,
  ledgerPostingList,
} from "../../redux/actions/action-creator";
import { useDropdownPosition } from "../Custom_Hooks/useDropdownPosition";
import { registerAltCFocusReturn } from "../../utils/altCFocusReturn";
import { dateFormateSend } from "../Date_Formate_Utilitiy/Index";

// Helper function to format balance with Cr/Dr
const formatBalance = (balance) => {
  if (
    balance === null ||
    balance === undefined ||
    balance === "" ||
    balance === 0 ||
    balance === "0"
  ) {
    return "0.00";
  }

  const numBalance = Number(balance);
  if (Number.isNaN(numBalance)) {
    return balance;
  }

  const abs = Math.abs(numBalance);
  const formatted = abs.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return numBalance < 0 ? `-${formatted} Cr` : `${formatted} Dr`;
};

const getLedgerDisplayBalance = (ledger) => {
  return ledger?.closing_balance ?? ledger?.opening_balance;
};

const formatBalanceForInput = (balance) => {
  const num = Number(balance) || 0;
  if (num === 0) return "0.00";

  const abs = Math.abs(num);
  const formatted = abs.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return num < 0 ? `-${formatted} Cr` : `${formatted} Dr`;
};

const DropdownLayout_1 = forwardRef(
  (
    {
      title,
      state,
      input_value,
      input_key,
      input_blc,
      input_id,
      rawData,
      data,
      setData,
      setSubmitConfirm,
      inputRefs,
      handleDropdownClick,
      className,
      index,
      loading,
      bypassFilter,
      skipTypeRecalc,   // ← NEW: when true, group change does NOT recalculate Dr/Cr type
    },
    ref,
  ) => {
    const dropdownRef = useRef(null);
    const dispatch = useDispatch();
    const { userData } = useSelector((state) => state.auth);
    const { company_session } = useSelector((state) => state.common);
    const coords = useDropdownPosition(state, ref);

    const resolveLedgerClosingBalance = useCallback(
      async (ledgerId) => {
        if (!ledgerId || !userData?.company_id) return null;

        try {
          const formData = new FormData();
          formData.append("company_id", userData.company_id);
          formData.append("ledger_id", ledgerId);
          formData.append(
            "fromDate",
            dateFormateSend(
              company_session?.start_financial_year ||
                company_session?.current_date,
            ),
          );
          formData.append(
            "toDate",
            dateFormateSend(company_session?.current_date),
          );

          const response = await dispatch(ledgerPostingList(formData));
          const opening = Number(response?.data?.opening_balance) || 0;
          const vouchers = response?.data?.voucherEntryList || [];

          const closing = vouchers.reduce((acc, row) => {
            const debit = Number(row?.total_debit_amount) || 0;
            const credit = Number(row?.total_credit_amount) || 0;
            return acc + debit - credit;
          }, opening);

          return closing;
        } catch (error) {
          return null;
        }
      },
      [
        dispatch,
        userData?.company_id,
        company_session?.start_financial_year,
        company_session?.current_date,
      ],
    );

    const { filteredData } = useDropdownFilter({
      dataList: rawData,
      searchInput: bypassFilter ? "" : input_value,
      inputRef: ref,
      isDynamic: false,
    });
    let is_dropdown_clicked = false;
    const { selectedItemIndex, setSelectedItemIndex } =
      useDropdownKeyboardNavigation({
        items: filteredData,
        state,
        inputRef: ref,
        onItemSelect: (item) => {
          is_dropdown_clicked = true;
          selectedDataHandle(item);
        },
        onItemAltCSelect: (item) => {
          // Close the current dropdown and open appropriate creation modal
          dispatch(
            closeModal(
              typeof index === "number" ? `${input_key}${index} ` : input_key,
            ),
          );

          // Determine which modal to open based on input_key
          let modalToOpen = "ledgerCreate";
          if (input_key === "godown_location" || input_key === "location") {
            modalToOpen = "godownCreateVoucher";
          } else if (input_key === "item_name") {
            // For item fields, open stock item creation modal
            modalToOpen = "stockItemCreate";
          } else if (input_key === "under_id" && input_id === "under") {
            // For stock group fields in stock item, open stock group creation modal
            modalToOpen = "create_under";
          } else if (input_key === "under_id" || input_key === "group_id") {
            // For group fields in ledger, open group creation modal
            modalToOpen = "create_group_under";
          } else if (input_key === "stock_cat") {
            modalToOpen = "create_stock_cat";
          } else if (input_key === "stock_company") {
            modalToOpen = "create_stock_company";
          } else if (
            input_key === "unit_name" ||
            input_key === "alt_unit_name"
          ) {
            modalToOpen = "create_unit";
          }

          registerAltCFocusReturn(modalToOpen);
          dispatch(openModal(modalToOpen));
        },
        input_value,
      });

    const selectedDataHandle = (item) => {
      const updatedData = { ...data };
      const itemName = item?.name || item?.ledger_name || item?.symbol;

      if (typeof index !== "number") {
        updatedData[input_key] = itemName;

        if (input_id) {
          updatedData[input_id] = item?.id; // Update with item id if input_id exists
        }
        if (input_blc) {
          updatedData[input_blc] =
            data.account === item.ledger_name
              ? data.account_ledger_balance
              : formatBalance(getLedgerDisplayBalance(item));
        }
      } else {
        // Update array fields within `data_item`
        updatedData.data_item = (data.data_item || []).map((item_data, i) =>
          i === index
            ? {
                ...item_data,
                [input_key]: itemName,
                ...(input_id ? { [input_id]: item?.id } : {}),
              }
            : item_data,
        );
      }

      if (input_id === "group_id") {
        updatedData["group_slug"] = item?.slug; // Add slug if input_id is 'group_id'
        updatedData["nature_of_group"] = item?.nature_of_group; // Add nature_of_group if input_id is 'group_id'
        // Only recalculate Dr/Cr type when NOT in alter mode (skipTypeRecalc prevents override)
        if (!skipTypeRecalc) {
          if (["Assets", "Expenses"].includes(item?.nature_of_group)) {
            updatedData["type"] = "Dr";
          } else if (["Liabilities", "Income"].includes(item?.nature_of_group)) {
            updatedData["type"] = "Cr";
          }
        }
      }
      if (input_id === "under") {
        updatedData["nature_of_group"] = item?.nature_of_group; // Add nature_of_group if input_id is 'group_id'
      }
      if (input_key === "unit_name") {
        updatedData["unit_name_first_unit"] = item?.first_unit
          ? item?.first_unit
          : "";
        updatedData["unit_name_second_unit"] = item?.second_unit
          ? item?.second_unit
          : "";
        updatedData["unit_name_third_unit"] = item?.third_unit
          ? item?.third_unit
          : "";
        updatedData["unit_name_fourth_unit"] = item?.fourth_unit
          ? item?.fourth_unit
          : "";
        updatedData["unit_name_fifth_unit"] = item?.fifth_unit
          ? item?.fifth_unit
          : "";
        updatedData["default_unit_id"] = item?.fifth_unit
          ? item?.fifth_unit
          : item?.fourth_unit
            ? item?.fourth_unit
            : item?.third_unit
              ? item?.third_unit
              : item?.second_unit
                ? item?.second_unit
                : item?.first_unit
                  ? item?.first_unit
                  : data?.default_unit_id
                    ? data?.default_unit_id
                    : "";
        updatedData["default_unit"] = item?.fifth_unit
          ? item?.fifth_unit
          : item?.fourth_unit
            ? item?.fourth_unit
            : item?.third_unit
              ? item?.third_unit
              : item?.second_unit
                ? item?.second_unit
                : item?.first_unit
                  ? item?.first_unit
                  : data?.default_unit
                    ? data?.default_unit
                    : "";
        updatedData["default_unit_rate_id"] = item?.fifth_unit
          ? item?.fifth_unit
          : item?.fourth_unit
            ? item?.fourth_unit
            : item?.third_unit
              ? item?.third_unit
              : item?.second_unit
                ? item?.second_unit
                : item?.first_unit
                  ? item?.first_unit
                  : data?.default_unit_rate_id
                    ? data?.default_unit_rate_id
                    : "";
        updatedData["default_unit_rate"] = item?.fifth_unit
          ? item?.fifth_unit
          : item?.fourth_unit
            ? item?.fourth_unit
            : item?.third_unit
              ? item?.third_unit
              : item?.second_unit
                ? item?.second_unit
                : item?.first_unit
                  ? item?.first_unit
                  : data?.default_unit_rate
                    ? data?.default_unit_rate
                    : "";
      }
      if (input_key === "party_bill_to") {
        const selectedLedgerId = item?.id;
        updatedData["party_bill_to_ledger_id"] = item?.id;
        updatedData["party_bill_to"] = item?.ledger_name || item?.name;
        updatedData["partry_bill_to_current_balance"] = formatBalance(
          getLedgerDisplayBalance(item),
        );
        updatedData["party_bill_to_mailing_name"] = item?.mailing_name;
        updatedData["bill_to_city"] = item?.city;
        updatedData["bill_to_contact"] = item?.mobile_no;
        updatedData["party_ship_to_ledger_id"] = item?.id;
        updatedData["party_ship_to"] = item?.ledger_name || item?.name;
        updatedData["party_ship_to_current_balance"] = formatBalance(
          getLedgerDisplayBalance(item),
        );
        updatedData["party_ship_to_mailing_name"] = item?.mailing_name;
        updatedData["ship_to_city"] = item?.city;
        updatedData["ship_to_contact"] = item?.mobile_no;

        resolveLedgerClosingBalance(selectedLedgerId).then((closingBalance) => {
          if (closingBalance === null) return;
          const formatted = formatBalanceForInput(closingBalance);

          setData((prev) => {
            if (
              String(prev?.party_bill_to_ledger_id) !== String(selectedLedgerId)
            ) {
              return prev;
            }

            return {
              ...prev,
              partry_bill_to_current_balance: formatted,
              party_ship_to_current_balance:
                String(prev?.party_ship_to_ledger_id) ===
                String(selectedLedgerId)
                  ? formatted
                  : prev?.party_ship_to_current_balance,
            };
          });
        });
      }
      if (input_key === "party_ship_to") {
        const selectedLedgerId = item?.id;
        updatedData["party_ship_to_ledger_id"] = item?.id;
        updatedData["party_ship_to"] = item?.ledger_name;
        updatedData["party_ship_to_current_balance"] = formatBalance(
          getLedgerDisplayBalance(item),
        );
        updatedData["party_ship_to_mailing_name"] = item?.mailing_name;
        updatedData["ship_to_city"] = item?.city;
        updatedData["ship_to_contact"] = item?.mobile_no;

        resolveLedgerClosingBalance(selectedLedgerId).then((closingBalance) => {
          if (closingBalance === null) return;
          const formatted = formatBalanceForInput(closingBalance);

          setData((prev) => {
            if (
              String(prev?.party_ship_to_ledger_id) !== String(selectedLedgerId)
            ) {
              return prev;
            }

            return {
              ...prev,
              party_ship_to_current_balance: formatted,
            };
          });
        });
      }
      if (input_key === "alt_unit_name") {
        const unitKeys = [
          "first_unit",
          "second_unit",
          "third_unit",
          "fourth_unit",
          "fifth_unit",
        ];
        unitKeys.forEach((key, _index) => {
          updatedData[`alt_unit_name_${key}`] = item?.[key]
            ? item?.[key]
            : "" || "";
        });
        dispatch(
          closeModal(
            typeof index === "number" ? `${input_key}${index} ` : input_key,
          ),
        );
      } else {
        dispatch(
          closeModal(
            typeof index === "number" ? `${input_key}${index} ` : input_key,
          ),
        );
      }

      setData(updatedData);

      // Contra account should always start from actual ledger closing balance.
      if (input_key === "account" && item?.id) {
        const selectedLedgerId = item.id;
        resolveLedgerClosingBalance(selectedLedgerId).then((closingBalance) => {
          if (closingBalance === null) return;
          const formatted = formatBalanceForInput(closingBalance);

          setData((prev) => {
            if (String(prev?.account_ledger_id) !== String(selectedLedgerId)) {
              return prev;
            }

            return {
              ...prev,
              account_ledger_balance: formatted,
            };
          });
        });
      }

      // Handle Navigation after selection if dynamic (has index)
      if (typeof index === "number") {
        const fields = ["godown_name", "quantity", "rate", "per", "value"];
        const fieldIdx = fields.indexOf(input_key);
        if (fieldIdx !== -1 && fieldIdx < fields.length - 1) {
          const nextField = fields[fieldIdx + 1];

          // Special skips for Not Applicable units
          if (
            data?.unit_name === "Not Applicable" &&
            input_key === "godown_name"
          ) {
            // This case might not be reached by DropdownLayout_1 but kept for consistency
          }

          const nextKey = `${nextField}${index} `;
          const nextRef =
            inputRefs?.current?.[nextKey] || inputRefs?.[nextKey]?.current;
          if (nextRef) {
            setTimeout(() => {
              nextRef.focus();
              if (nextRef.setSelectionRange)
                nextRef.setSelectionRange(0, nextRef.value?.length || 0);
            }, 50);
          }
        }
      }

      if (handleDropdownClick) {
        handleDropdownClick(
          input_key,
          item?.ledger_name || item?.name || item?.symbol,
        );
      }
    };

    useDropdownScroll({ dropdownRef, selectedItemIndex });

    return (
      <div
        className={`${className ? className : "dropdown-content-list"} ${state ? "show-list" : ""}`}
        style={{
          ...coords,
          display: state ? "flex" : "none",
          flexDirection: "column",
          overflow: "hidden",
          padding: 0,
          maxHeight: coords.maxHeight || "87vh",
          height: "fit-content",
        }}
      >
        <div
          style={{
            background: "skyblue",
            position: "sticky",
            top: 0,
            flexShrink: 0,
            padding: "2px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            textAlign: "center",
            lineHeight: "1.5",
          }}
        >
          {title}
        </div>

        <div id="data" ref={dropdownRef} style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center p-3">
              <i className="fas fa-spinner fa-spin me-2"></i>
              <span>Loading...</span>
            </div>
          ) : (
            Array.isArray(filteredData) &&
            filteredData?.map((key, number) => {
              const name = key?.ledger_name || key?.name || key?.symbol;
              const formal_name = key?.formal_name;
              const type = key?.type;
              const balance = key?.opening_balance;
              const id = key?.id;
              const index = name
                ?.toLowerCase()
                ?.indexOf(input_value?.toLowerCase());
              return (
                <Link
                  className={`list  ${
                    number === selectedItemIndex ? "selected" : ""
                  } ${
                    ["primary", "not applicable", "not available"].includes(
                      name?.toLowerCase(),
                    )
                      ? "primary-icon"
                      : ""
                  }`}
                  id="list"
                  key={number}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedItemIndex(number);
                    const item = filteredData[number];
                    selectedDataHandle(item);
                  }}
                >
                  <div className="row m-0">
                    <div className={formal_name ? "col-6 p-0" : "col-12 p-0"}>
                      {name === "End of List" ? (
                        <>
                          <i
                            className="fas fa-diamond me-2"
                            style={{
                              fontSize: "7px",
                              verticalAlign: "middle",
                              height: "12px",
                            }}
                          ></i>
                          {index !== -1 ? (
                            <>
                              {name?.substring(0, index)}
                              <span style={{ color: "red" }}>
                                {name?.substring(
                                  index,
                                  index + input_value?.length,
                                )}
                              </span>
                              {name?.substring(index + input_value?.length)}
                            </>
                          ) : (
                            name
                          )}
                        </>
                      ) : index !== -1 ? (
                        <>
                          {name?.substring(0, index)}
                          <span style={{ color: "red" }}>
                            {name?.substring(
                              index,
                              index + input_value?.length,
                            )}
                          </span>
                          {name?.substring(index + input_value?.length)}
                        </>
                      ) : (
                        name
                      )}
                    </div>
                    {formal_name ? (
                      <div className="col-6">
                        {formal_name && type === "Simple"
                          ? `(${formal_name})`
                          : null}
                      </div>
                    ) : null}
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    );
  },
);

export default React.memo(DropdownLayout_1);
